import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import StarRating from "./StarRating";
import { toast } from "react-toastify";
import useAuth from "../hooks/useAuth";
import { FaEdit, FaStar } from "react-icons/fa";
import { PROXY } from "../lib/utils";
import { IoTrashSharp } from "react-icons/io5";
import DeleteReviewModal from "./DeleteReviewModal";
import { Textarea } from "@material-tailwind/react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import ReviewRatingInput from "./ReviewRatingInput";
import { isDuplicateReviewContent } from "../lib/reviewUtils";


const ReviewCard = ({ mutate, courseId, reviews }) => {
    const [loading, setLoading] = useState(false);
    const [editRating, setEditRating] = useState(0);
    const [editContent, setEditContent] = useState("");
    const [editReviewId, setEditReviewId] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedReviewId, setSelectedReviewId] = useState(null);
    const { t } = useTranslation();
    const location = useLocation();
    useEffect(() => {
        if (!location.hash.startsWith("#review-")) return;
        const reviewId = location.hash.slice("#review-".length);
        if (!reviews.some((review) => String(review._id) === reviewId)) return;
        const frame = requestAnimationFrame(() => document.getElementById(`review-${reviewId}`)?.scrollIntoView({ block: "center" }));
        return () => cancelAnimationFrame(frame);
    }, [location.hash, reviews]);
    
    const { user, isAuth, accessToken } = useAuth();
    const actionButtonClass = "inline-flex items-center justify-center gap-2 rounded-full border px-3 py-2 text-xs font-semibold shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2";
    const editPrimaryButtonClass = "inline-flex items-center justify-center gap-2 rounded-full border border-indigo-600 bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60";
    const editSecondaryButtonClass = "inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60";

    const handleEditClick = (review) => {
        setEditReviewId(review._id);
        setEditContent(review.content);
        setEditRating(review.rating);
    };

    const handleDeleteClick = (reviewId) => {
        setSelectedReviewId(reviewId);
        setShowDeleteModal(true);
    };

    const handleCancelEdit = () => {
        setEditReviewId(null);
        setEditContent("");
        setEditRating(0);
    };

    const handleEditSubmit = async () => {
        if (!editContent || editRating < 1 || editRating > 5) {
            toast.error(t("toast.review-valid-content"), { position: "top-center" }); 
            return;
        }

        if (isDuplicateReviewContent({
            reviews,
            userId: user?.id,
            content: editContent,
            excludeReviewId: editReviewId,
        })) {
            toast.warn(t("validation.review_duplicate"), { position: "top-center" });
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${PROXY}/courses/${courseId}/reviews/${editReviewId}`, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    content: editContent,
                    rating: editRating,
                }),
            });

            const result = await response.json();

            if (response.ok) {
                toast.success(t("toast.review-update-success"), { position: "top-center" });
                mutate();
                handleCancelEdit();
            } else if (response.status === 409 || result?.code === "DUPLICATE_REVIEW") {
                toast.warn(result.message || t("validation.review_duplicate"), { position: "top-center" });
            } else {
                toast.error(result.message || t('toast.review-update-failed'), { position: "top-center" });
            }
        } catch (error) {
            toast.error(t('toast.generic'), { position: "top-center" });
        } finally {
            setLoading(false);
            mutate()
        }
    };

    return (
        <>
            <div className="flex flex-col gap-4 mt-5 mb-20">
                {reviews.map((review) => (
                    <article id={`review-${review._id}`} key={review._id} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm target:border-indigo-500 target:ring-2 target:ring-indigo-300">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-col items-start gap-2">
                                    <div>
                                        <span className="block text-lg font-semibold text-slate-900">{review?.user?.fullName}</span>
                                        <span className="block text-xs text-slate-500">
                                            {moment(review?.createdAt).format("YYYY D MMMM")}
                                        </span>
                                    </div>

                                    {editReviewId === review._id ? (
                                        <div className="w-full max-w-sm rounded-2xl border border-amber-100 bg-amber-50/70 p-3">
                                            <ReviewRatingInput
                                                value={editRating}
                                                onChange={setEditRating}
                                                label={t("ReviewForm.SelectRating")}
                                                disabled={loading}
                                            />
                                        </div>
                                    ) : (
                                        <StarRating averageRating={review?.rating} />
                                    )}
                                </div>
                            </div>

                            {isAuth && user?.id === review?.user?._id && (
                                <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                                    {editReviewId === review._id ? (
                                        <>
                                            <button
                                                type="button"
                                                className={editPrimaryButtonClass}
                                                onClick={handleEditSubmit}
                                                disabled={loading}
                                            >
                                                {loading ? t("Saving") : t("Save")}
                                            </button>
                                            <button
                                                type="button"
                                                className={editSecondaryButtonClass}
                                                onClick={handleCancelEdit}
                                                disabled={loading}
                                            >
                                                {t("Cancel")}
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                title={t('Edit')}
                                                className={`${actionButtonClass} border-slate-200 bg-white text-slate-700 hover:border-indigo-200 hover:text-indigo-700`}
                                                onClick={() => handleEditClick(review)}
                                            >
                                                <FaEdit size={13} className="text-indigo-500" />
                                                <span>{t("Edit-your-review")}</span>
                                            </button>
                                            <button
                                                type="button"
                                                title={t('Delete')}
                                                className={`${actionButtonClass} border-red-100 bg-white text-red-600 hover:border-red-200 hover:bg-red-50`}
                                                onClick={() => handleDeleteClick(review._id)}
                                            >
                                                <IoTrashSharp size={13} />
                                                <span>{t("Delete")}</span>
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {editReviewId === review._id ? (
                            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                                <div className="mb-3 flex items-center gap-2 text-amber-500">
                                    {[...Array(5)].map((_, index) => (
                                        <FaStar key={index} size={14} />
                                    ))}
                                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                                        {t("Edit-your-review")}
                                    </span>
                                </div>
                                <Textarea
                                    label={t("Edit-your-review")}
                                    value={editContent}
                                    onChange={(e) => setEditContent(e.target.value)}
                                    className="min-h-28"
                                    error={!editContent}
                                    color="indigo"
                                />
                            </div>
                        ) : (
                            <p className="mt-4 text-sm leading-7 text-slate-600">{review?.content}</p>
                        )}
                    </article>
                ))}
            </div>

            {showDeleteModal && (
                <DeleteReviewModal
                    onClose={() => setShowDeleteModal(false)}
                    showModal={showDeleteModal}
                    mutate={mutate}
                    courseId={courseId}
                    reviewId={selectedReviewId}
                />
            )}
        </>
    );
};

export default ReviewCard;
